import styled from "styled-components";
import Calendar from "../../Inputs/Calendar";
import React, { useEffect, useRef, useState } from "react";
import Dropdown from "../../Dropdown";
import Button, { TButtonIconVariants, TButtonProps } from "../../Button";
import { format } from "date-fns/format";
import { add } from "date-fns/add";
import { parseISO } from "date-fns/parseISO";
import {
  invoiceStatusOptions,
  natureOptions,
  TStatus,
} from "../../../../pages/IssuedInvoices/mock";
import ToggleSwitch from "../../Inputs/ToggleSwitch";
import { subMonths } from "date-fns/subMonths";
import { startOfMonth } from "date-fns/startOfMonth";
import { endOfMonth } from "date-fns/endOfMonth";
import {
  TIssuedExportedInvoice,
  TNature,
} from "../../../../pages/IssuedInvoices/Export/mock";

// De-para rótulo -> inteiro esperado pelo backend
// (issued_invoices.models.IssuedInvoice.EmissionStatus / Nature).
const STATUS_LABEL_TO_VALUE: Record<string, number> = {
  "Em processamento": 10,
  Autorizada: 20,
  "Autorizada em contingência": 30,
  Rejeitada: 40,
  Denegada: 50,
  Cancelada: 60,
  Inutilizada: 70,
};

const NATURE_LABEL_TO_VALUE: Record<string, number> = {
  "001 - Compra": 1,
  "002 - Venda": 2,
  "003 - Transferência": 3,
  "004 - Devolução": 4,
  "005 - Importação": 5,
  "006 - Exportação": 6,
  "007 - Remessa": 7,
  "008 - Retorno": 8,
  "009 - Outras entradas": 9,
  "010 - Outras saídas": 10,
  "011 - Serviços - ISSQN": 11,
  "012 - Lançamento de Crédito": 12,
  "013 - Recebimento": 13,
  "014 - Ressarcimento": 14,
  "015 - Serviço - ICMS": 15,
};

// Operador: 10 = "Iguais a" (EQ), 20 = "Diferentes de" (NEQ).
const OPERATOR_LABEL_TO_VALUE: Record<string, number> = {
  "Iguais a": 10,
  "Diferentes de": 20,
};

// Formato enviado ao backend (issued_invoices export serializer).
export type TIssuedExportFormValues = {
  start_date?: string;
  end_date?: string;
  status_operator?: number;
  filter_status?: number[];
  nature_operator?: number;
  filter_nature?: number[];
  export_files?: string[];
};

const Container = styled.form`
  & > p,
  label,
  b {
    font-size: 16px;
    color: var(--color-text-paragraph);
    font-family: var(--font-paragraph-default);
  }

  b {
    font-family: var(--font-paragraph-bold);
  }

  label {
    font-size: 14px;
  }

  table {
    thead tr:first-child {
      th b {
        font-size: 14px;
      }
    }

    thead tr:nth-child(2) {
      th b {
        font-size: 12px;
      }
    }
  }
`;

const RowContainer = styled.div<{
  $hasModeError?: "hasError" | "noError";
  $isPaid?: boolean;
}>`
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;

  margin-top: 16px;

  position: relative;

  h2 {
    font-size: 14px;
    font-family: var(--font-paragraph-bold);
    color: var(--color-text-paragraph);
  }

  .advanced-filter-container {
    margin-top: 8px;

    display: flex;
    gap: 16px;

    button {
      width: 522px;
    }

    menu {
      width: 522px;
      & button {
        width: 504px;
      }
    }

    & > div:first-child {
      width: 146px;

      & div button {
        p.content {
          top: 14px;
        }

        width: 146px;
      }
    }
  }
`;

const Footer = styled.footer`
  margin-top: 30px;
  border-top: 1px solid var(--color-light-200);
  padding: 24px 0px 0px;
  display: flex;
  justify-content: flex-end;

  .textContainer {
    b,
    p {
      font-size: 14px;
      color: var(--color-text-paragraph);
    }

    b {
      font-family: var(--font-paragraph-bold);
    }

    p {
      font-family: var(--font-paragraph-default);
    }
  }

  .buttonsContainer {
    display: flex;
    justify-content: flex-end;

    & button:not(& button:last-child) {
      margin-right: 12px;
    }
  }
`;

type TExportIssuedInvoiceProps = {
  invoice?: TIssuedExportedInvoice;
  footer: { buttons: TButtonProps<TButtonIconVariants>[] };
  onChange?: (values: TIssuedExportFormValues) => void;
};

const ExportIssuedInvoice: React.FC<TExportIssuedInvoiceProps> = ({
  invoice,
  footer,
  onChange,
}) => {
  const exportedInvoiceRef = useRef<TIssuedExportedInvoice | undefined>(
    invoice
  );
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");

  const today = new Date();
  const minDate = format(add(today, { days: -180 }), "dd/MM/yyyy");
  const maxDate = format(today, "dd/MM/yyyy");

  const [includeAll, setIncludeAll] = useState({
    status: true,
    nature: true,
  });

  // Operadores "Iguais a" / "Diferentes de" para status e natureza.
  const [statusOperator, setStatusOperator] = useState<string>("Iguais a");
  const [natureOperator, setNatureOperator] = useState<string>("Iguais a");

  // Example: initial state for status and natureza dropdowns
  const [selectedStatus, setSelectedStatus] = useState<TStatus[]>(
    exportedInvoiceRef.current?.status || []
  );
  const [selectedNature, setSelectedNature] = useState<TNature[]>(
    exportedInvoiceRef.current?.nature || []
  );

  // Handler for toggling a specific filter section
  const handleToggle = (key: keyof typeof includeAll, value: boolean) => {
    setIncludeAll((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  useEffect(() => {
    if (exportedInvoiceRef.current?.start_date) {
      setStartDate(
        format(parseISO(exportedInvoiceRef.current.start_date), "dd/MM/yyyy")
      );
    } else {
      const lastMonth = subMonths(new Date(), 1);
      setStartDate(format(startOfMonth(lastMonth), "dd/MM/yyyy"));
    }
    if (exportedInvoiceRef.current?.end_date) {
      setEndDate(
        format(parseISO(exportedInvoiceRef.current.end_date), "dd/MM/yyyy")
      );
    } else {
      const lastMonth = subMonths(new Date(), 1);
      setEndDate(format(endOfMonth(lastMonth), "dd/MM/yyyy"));
    }
  }, [invoice]);

  useEffect(() => {
    if (invoice) {
      setIncludeAll({
        status: invoice?.status ? invoice.status.length === 0 : true,
        nature: invoice?.nature ? invoice.nature.length === 0 : true,
      });
    }
  }, []);

  // Converte dd/MM/yyyy -> yyyy-MM-dd para o backend.
  const toIsoDate = (value: string) => {
    if (!value) return undefined;
    const [d, m, y] = value.split("/");
    if (!d || !m || !y) return undefined;
    return `${y}-${m}-${d}`;
  };

  // Reporta ao componente pai a cada mudança de estado o payload do backend.
  useEffect(() => {
    if (!onChange) return;
    onChange({
      start_date: toIsoDate(startDate),
      end_date: toIsoDate(endDate),
      status_operator: includeAll.status
        ? undefined
        : OPERATOR_LABEL_TO_VALUE[statusOperator],
      filter_status: includeAll.status
        ? []
        : selectedStatus
            .map((s) => STATUS_LABEL_TO_VALUE[s])
            .filter((v) => v !== undefined),
      nature_operator: includeAll.nature
        ? undefined
        : OPERATOR_LABEL_TO_VALUE[natureOperator],
      filter_nature: includeAll.nature
        ? []
        : selectedNature
            .map((n) => NATURE_LABEL_TO_VALUE[n])
            .filter((v) => v !== undefined),
      export_files: ["xml", "pdf"],
    });
  }, [
    startDate,
    endDate,
    includeAll.status,
    includeAll.nature,
    statusOperator,
    natureOperator,
    selectedStatus,
    selectedNature,
  ]);

  return (
    <Container
      onSubmit={(e) => {
        e.preventDefault();
      }}
    >
      <RowContainer style={{ marginTop: "0px" }}>
        <Calendar
          key={startDate + endDate}
          variant="period"
          placeholder="Selecionar período"
          hasMandatoryStyle={{ marginLeft: "127px" }}
          className="calendar"
          filled={{ startDate, endDate }}
          minDate={minDate}
          maxDate={maxDate}
          onSelect={(selectDate) => {
            // variant="period" entrega { startDate, endDate } em dd/MM/yyyy
            if (selectDate && typeof selectDate === "object") {
              if (selectDate.startDate) setStartDate(selectDate.startDate);
              if (selectDate.endDate) setEndDate(selectDate.endDate);
            }
          }}
        />
      </RowContainer>
      <RowContainer>
        <h2>Status</h2>

        <div style={{ marginTop: "8px" }}>
          <ToggleSwitch
            key={includeAll.status + ""}
            id="automation-process-status-selector"
            initialState={includeAll.status}
            onClick={(value) => handleToggle("status", value)}
          />
          <label htmlFor="automation-process-status-selector">
            &nbsp;Todos os status &nbsp;
          </label>
        </div>
        {!includeAll.status && (
          <div className="advanced-filter-container">
            <Dropdown
              button={{
                variant: "unlabeled",
                labels: ["Iguais a", "Diferentes de"],
                placeholder: "",
                //
              }}
              menu={{
                variant: "unlabeled",
                options: ["Iguais a", "Diferentes de"],
                search: false,
                shouldHideActiveIcon: true,
                onSelect(selected) {
                  setStatusOperator(selected);
                },
              }}
            />
            <Dropdown
              key={selectedStatus.length}
              button={{
                selectType: "multi",
                variant: "labeled",
                labels: [],
                placeholder: "Selecionar status",
                initialMultiSelectLabels: selectedStatus,
                onRemove(_removedTag, updatedSelected) {
                  setSelectedStatus(updatedSelected as TStatus[]);
                },
              }}
              menu={{
                variant: "unlabeled",
                options: invoiceStatusOptions,
                search: false,
                shouldHideActiveIcon: true,
                forceSelectOptionsByIndex: selectedStatus
                  ? selectedStatus
                      .map((status) => invoiceStatusOptions.indexOf(status))
                      .filter((idx) => idx !== -1)
                  : [],
                onSelect(selected) {
                  // toggle do rótulo clicado
                  setSelectedStatus((prev) =>
                    prev.includes(selected as TStatus)
                      ? prev.filter((s) => s !== selected)
                      : [...prev, selected as TStatus]
                  );
                },
              }}
            />
          </div>
        )}
      </RowContainer>
      <RowContainer>
        <h2>Natureza</h2>

        <div style={{ marginTop: "8px" }}>
          <ToggleSwitch
            key={includeAll.nature + ""}
            id="automation-process-nature-selector"
            initialState={includeAll.nature}
            onClick={(value) => handleToggle("nature", value)}
          />
          <label htmlFor="automation-process-nature-selector">
            &nbsp;Todas as naturezas de operação &nbsp;
          </label>
        </div>
        {!includeAll.nature && (
          <div className="advanced-filter-container">
            <Dropdown
              button={{
                variant: "unlabeled",
                labels: ["Iguais a", "Diferentes de"],
                placeholder: "",
                //
              }}
              menu={{
                variant: "unlabeled",
                options: ["Iguais a", "Diferentes de"],
                search: false,
                shouldHideActiveIcon: true,
                onSelect(selected) {
                  setNatureOperator(selected);
                },
              }}
            />
            <Dropdown
              key={selectedNature.length}
              button={{
                selectType: "multi",
                variant: "labeled",
                labels: [],
                placeholder: "Selecionar natureza",
                initialMultiSelectLabels: selectedNature,
                onRemove(_removedTag, updatedSelected) {
                  setSelectedNature(updatedSelected as TNature[]);
                },
              }}
              menu={{
                variant: "unlabeled",
                options: natureOptions,
                search: false,
                shouldHideActiveIcon: true,
                forceSelectOptionsByIndex: selectedNature
                  ? selectedNature
                      .map((nature) => natureOptions.indexOf(nature))
                      .filter((idx) => idx !== -1)
                  : [],
                onSelect(selected) {
                  setSelectedNature((prev) =>
                    prev.includes(selected as TNature)
                      ? prev.filter((n) => n !== selected)
                      : [...prev, selected as TNature]
                  );
                },
              }}
            />
          </div>
        )}
      </RowContainer>
      <Footer>
        <div className="buttonsContainer">
          {footer.buttons?.map((buttonProps, index) => {
            return (
              <Button
                key={index}
                role={index === 1 ? "submit" : undefined}
                {...buttonProps}
              />
            );
          })}
        </div>
      </Footer>
    </Container>
  );
};

export default ExportIssuedInvoice;
